"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AppChromeRouter } from "@/components/layout/AppChromeRouter";
import { TelephonyHost } from "@/components/telephony/TelephonyHost";
import "@/styles/workspace-shell.css";

type PanelWorkspaceShellProps = {
  children: React.ReactNode;
};

const WORKSPACE_PARAM = "__workspace";
const SHELL_PARAM = "__shell";
const SHELL_STORAGE_KEY = "farktor:workspace-shell";
const WORKSPACE_ROUTE_EVENT = "farktor:workspace-route";

function shouldBypassShell(pathname: string) {
  return (
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname === "/demo" ||
    pathname.startsWith("/demo/") ||
    pathname === "/embed" ||
    pathname.startsWith("/embed/") ||
    pathname === "/support" ||
    pathname.startsWith("/support/")
  );
}

function buildWorkspaceSrc() {
  const url = new URL(window.location.href);
  url.searchParams.set(WORKSPACE_PARAM, "1");
  url.searchParams.delete(SHELL_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}

function isShellEnabled() {
  if (process.env.NEXT_PUBLIC_PANEL_WORKSPACE_SHELL !== "1") return false;
  const url = new URL(window.location.href);
  const shellParam = url.searchParams.get(SHELL_PARAM);
  if (shellParam === "0") return false;
  if (shellParam === "1") return true;
  return window.localStorage.getItem(SHELL_STORAGE_KEY) === "1";
}

function publicUrlFromWorkspace(frameLocation: Location) {
  const url = new URL(frameLocation.href);
  url.searchParams.delete(WORKSPACE_PARAM);
  url.searchParams.delete(SHELL_PARAM);
  return `${url.pathname}${url.search}${url.hash}` || "/";
}

function currentPublicWorkspaceUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete(WORKSPACE_PARAM);
  url.searchParams.delete(SHELL_PARAM);
  return `${url.pathname}${url.search}${url.hash}` || "/";
}

function WorkspaceFrameBridge() {
  useEffect(() => {
    if (window.parent === window) return;

    const notifyParent = () => {
      window.parent.postMessage(
        { type: WORKSPACE_ROUTE_EVENT, url: currentPublicWorkspaceUrl() },
        window.location.origin,
      );
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      window.setTimeout(notifyParent, 0);
      return result;
    };

    window.history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      window.setTimeout(notifyParent, 0);
      return result;
    };

    window.addEventListener("popstate", notifyParent);
    window.addEventListener("hashchange", notifyParent);
    notifyParent();

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", notifyParent);
      window.removeEventListener("hashchange", notifyParent);
    };
  }, []);

  return null;
}

export function PanelWorkspaceShell({ children }: PanelWorkspaceShellProps) {
  const pathname = usePathname() || "";
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [workspaceSrc, setWorkspaceSrc] = useState("");
  const [isWorkspaceFrame, setIsWorkspaceFrame] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const url = new URL(window.location.href);
      setMounted(true);
      setIsWorkspaceFrame(url.searchParams.get(WORKSPACE_PARAM) === "1");
      setWorkspaceSrc(buildWorkspaceSrc());
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const shellActive = useMemo(() => {
    if (!mounted || typeof window === "undefined") return false;
    const url = new URL(window.location.href);
    if (url.searchParams.get(WORKSPACE_PARAM) === "1") return false;
    if (shouldBypassShell(url.pathname)) return false;
    return isShellEnabled();
  }, [mounted]);

  const syncShellUrl = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow) return;
    try {
      const nextUrl = publicUrlFromWorkspace(frame.contentWindow.location);
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextUrl !== currentUrl) window.history.replaceState(null, "", nextUrl);
    } catch {
      // Same-origin iframe should be readable; ignore while it is still navigating.
    }
  }, []);

  useEffect(() => {
    if (!shellActive) return;
    const syncRouteFromFrame = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== WORKSPACE_ROUTE_EVENT) return;
      const nextUrl = String(event.data.url || "/");
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextUrl !== currentUrl) window.history.replaceState(null, "", nextUrl);
    };
    window.addEventListener("message", syncRouteFromFrame);
    return () => window.removeEventListener("message", syncRouteFromFrame);
  }, [shellActive]);

  useEffect(() => {
    if (!shellActive) return;
    const syncFrameFromShell = () => setWorkspaceSrc(buildWorkspaceSrc());
    window.addEventListener("popstate", syncFrameFromShell);
    window.addEventListener("hashchange", syncFrameFromShell);
    return () => {
      window.removeEventListener("popstate", syncFrameFromShell);
      window.removeEventListener("hashchange", syncFrameFromShell);
    };
  }, [shellActive]);

  if (!shellActive) {
    return (
      <>
        <AppChromeRouter>{children}</AppChromeRouter>
        {isWorkspaceFrame ? <WorkspaceFrameBridge /> : shouldBypassShell(pathname) ? null : <TelephonyHost />}
      </>
    );
  }

  return (
    <div className={`panel-workspace-shell ${phoneOpen ? "is-phone-open" : ""}`}>
      <div className="panel-workspace-shell__workspace">
        {workspaceSrc ? (
          <iframe
            ref={iframeRef}
            src={workspaceSrc}
            className="panel-workspace-shell__frame"
            title="Farktor Panel"
            onLoad={syncShellUrl}
          />
        ) : null}
      </div>
      <aside className="panel-workspace-shell__phone" aria-label="Telefon">
        <TelephonyHost mode="dock" onOpenChange={setPhoneOpen} />
      </aside>
    </div>
  );
}
